import type {UserApiModel} from '../api/user';
import type {ApiRpcRequest} from '../api/utils/apiRequest/types';
import type {Nullable} from './utility-types';

import {
  ErrorJwtExpired,
  ErrorResourceUnreachable,
  ErrorResourceUserBlockedAccount,
  ErrorResourceUserExpiredCode,
  ErrorResourceUserInvalidCode,
  ErrorResourceUserTooManyAttempts,
  ErrorRpcInvalidParams,
  ErrorSubscriptionExpired,
  RpcRequestError,
} from './errors/api';
import {
  AppError,
  CancelRequestError,
  HttpRequestError,
  NoConnectionError,
} from './errors/app';
import {isTestEnv} from './test-utils/isTestEnv';

export interface ILogger {
  error?: (error: any) => void;
  event?: (name: string, params?: {[key: string]: number | string}) => void;
  init?: () => void;
  logApiRequest?: (payload: ApiRpcRequest | ApiRpcRequest[]) => void;
  pageView?: (to: string, from?: string) => void;
  setSuperProperties?: (properties: Record<string, number | string>) => void;
  setUser?: (user: Nullable<UserApiModel>) => void;
  setUserProperty?: (prop: string, value: any) => void;
}

export let logger: InstanceType<typeof YCLogger>;

class YCLogger implements ILogger {
  private logger?: ILogger;

  constructor(ilogger?: ILogger) {
    this.logger = ilogger;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    logger = this;
  }

  init() {
    this.logger?.init?.();
  }

  __setPlatformLogger(platformLogger: ILogger) {
    this.logger = platformLogger;
  }

  error(error: any) {
    if (
      error instanceof RpcRequestError ||
      isNotFixableRpcError(error) ||
      isErrorToSkip(error)
    ) {
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      /* eslint-disable no-console */
      console.warn('-------LOGGER.ERROR-------');
      console.warn(error);
      console.warn('--------------------------');
      /* eslint-enable no-console */
    }

    if (error instanceof AppError) {
      this.logger?.error?.(error.toLoggerError());
    } else {
      this.logger?.error?.(error);
    }
  }

  event(name: string, params?: {[key: string]: number | string}) {
    if (this.logger?.event) {
      return this.logger.event(name, params);
    }

    if (!isTestEnv) {
      // eslint-disable-next-line no-console
      console.warn('logger.event - not implemented', name, params);
    }
  }

  logApiRequest(payload: ApiRpcRequest | ApiRpcRequest[]) {
    if (this.logger?.logApiRequest) {
      return this.logger?.logApiRequest(payload);
    }

    if (!isTestEnv) {
      // eslint-disable-next-line no-console
      console.warn('logger.logApiRequestImpl - not implemented', payload);
    }
  }

  pageView(to: string, from?: string) {
    if (this.logger?.pageView) {
      return this.logger?.pageView(to, from);
    }

    if (!isTestEnv) {
      // eslint-disable-next-line no-console
      console.warn('logger.pageView - not implemented', to, from);
    }
  }

  rpcError(error: RpcRequestError) {
    if (isErrorToSkip(error) || isNotFixableRpcError(error)) {
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      /* eslint-disable no-console */
      console.warn('-------LOGGER.RPC_ERROR-------');
      console.warn(error);
      console.warn('--------------------------');
      /* eslint-enable no-console */
    }

    this.logger?.error?.(error.toLoggerError({ignoreStack: true}));
  }

  setSuperProperties(props: Record<string, number | string>) {
    if (this.logger?.setSuperProperties) {
      return this.logger.setSuperProperties(props);
    }

    if (!isTestEnv) {
      // eslint-disable-next-line no-console
      console.warn('logger.setSuperProperties - not implemented', props);
    }
  }

  setUser(user: Nullable<UserApiModel>) {
    if (this.logger?.setUser) {
      return this.logger.setUser(user);
    }

    if (!isTestEnv) {
      // eslint-disable-next-line no-console
      console.warn('logger.setUser - not implemented', user);
    }
  }

  setUserProperty(prop: string, value: any) {
    if (this.logger?.setUserProperty) {
      return this.logger.setUserProperty(prop, value);
    }

    if (!isTestEnv) {
      // eslint-disable-next-line no-console
      console.warn('logger.setUserProperty - not implemented', prop, value);
    }
  }
}

logger = new YCLogger();

function isErrorToSkip(error: any) {
  return (
    !error ||
    error instanceof ErrorSubscriptionExpired ||
    error instanceof CancelRequestError ||
    error instanceof NoConnectionError ||
    HttpRequestError.isBanByIp(error) ||
    HttpRequestError.isAmazonCodeDeploy(error)
  );
}

function isNotFixableRpcError(error: RpcRequestError) {
  return (
    error instanceof ErrorResourceUserExpiredCode ||
    // we need to observe logouts problem for some time
    // error instanceof ErrorResourceUserExpiredSession ||
    error instanceof ErrorResourceUserInvalidCode ||
    error instanceof ErrorResourceUserTooManyAttempts ||
    error instanceof ErrorResourceUserBlockedAccount ||
    (error instanceof ErrorJwtExpired && error.data?.sub === 'access') ||
    (error instanceof ErrorResourceUnreachable &&
      error.data?.method?.includes('programs.read')) ||
    ((error instanceof ErrorResourceUnreachable ||
      error instanceof ErrorRpcInvalidParams) &&
      error.data?.method === 'user.coupons.read')
  );
}
